import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, formatDistanceToNow, isPast, isToday } from "date-fns";
import { Bell, Plus, Calendar as CalendarIcon, Trash2, Heart, Clock, AlertCircle, Repeat } from "lucide-react";

const REMINDER_TYPES = [
  { value: "anniversary", label: "Anniversary", icon: Heart },
  { value: "deadline", label: "Deadline", icon: Clock },
  { value: "custom", label: "Custom", icon: Bell },
];

export const RemindersPanel = ({ isOpen, onClose }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    date: null,
    reminder_type: "anniversary",
    recurring: false,
    notes: ""
  });

  const fetchReminders = async () => {
    try {
      const res = await axios.get(`${API}/reminders`);
      setReminders(res.data);
    } catch (error) {
      console.error("Failed to fetch reminders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReminders();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.date) {
      toast.error("Please fill in title and date");
      return;
    }

    try {
      await axios.post(`${API}/reminders`, {
        ...formData,
        date: format(formData.date, "yyyy-MM-dd")
      });
      toast.success("Reminder created!");
      setFormData({ title: "", date: null, reminder_type: "anniversary", recurring: false, notes: "" });
      setShowAddForm(false);
      fetchReminders();
    } catch (error) {
      toast.error("Failed to create reminder");
    }
  };

  const handleDelete = async (reminderId) => {
    try {
      await axios.delete(`${API}/reminders/${reminderId}`);
      setReminders(reminders.filter(r => r.id !== reminderId));
      toast.success("Reminder deleted");
    } catch (error) {
      toast.error("Failed to delete reminder");
    }
  };

  const getReminderStatus = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) return { label: "Today!", color: "text-primary", bg: "bg-primary/10" };
    if (isPast(date)) return { label: "Past", color: "text-muted-foreground", bg: "bg-muted" };
    return { label: formatDistanceToNow(date, { addSuffix: true }), color: "text-charcoal", bg: "bg-secondary/10" };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-none rounded-2xl p-0 max-h-[85vh] flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Reminders & Anniversaries
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[60vh]">
          <div className="p-6 space-y-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : showAddForm ? (
              <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-background rounded-xl border border-charcoal/10">
                <div className="space-y-2">
                  <Label className="text-charcoal font-medium">Title</Label>
                  <Input
                    placeholder="e.g., Our Anniversary"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-card border-charcoal/10 rounded-lg"
                    data-testid="reminder-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-charcoal font-medium">Date</Label>
                  <Popover modal={true}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-11 justify-start text-left font-normal bg-card border-charcoal/10 rounded-lg"
                        data-testid="reminder-date-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                        {formData.date ? format(formData.date, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.date}
                        onSelect={(date) => setFormData({ ...formData, date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-charcoal font-medium">Type</Label>
                  <Select
                    value={formData.reminder_type}
                    onValueChange={(value) => setFormData({ ...formData, reminder_type: value })}
                  >
                    <SelectTrigger className="h-11 bg-card border-charcoal/10 rounded-lg" data-testid="reminder-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REMINDER_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-3 bg-card rounded-lg">
                  <div className="flex items-center gap-2">
                    <Repeat className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-charcoal">Repeat Yearly</span>
                  </div>
                  <Switch
                    checked={formData.recurring}
                    onCheckedChange={(checked) => setFormData({ ...formData, recurring: checked })}
                    data-testid="reminder-recurring-switch"
                  />
                </div>

                <Textarea
                  placeholder="Notes (optional)"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-card border-charcoal/10 rounded-lg resize-none"
                  rows={2}
                  data-testid="reminder-notes-input"
                />

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddForm(false);
                      setFormData({ title: "", date: null, reminder_type: "anniversary", recurring: false, notes: "" });
                    }}
                    className="flex-1 rounded-lg border-charcoal/10"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-lg"
                    data-testid="save-reminder-btn"
                  >
                    Save Reminder
                  </Button>
                </div>
              </form>
            ) : reminders.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-medium text-charcoal mb-2">No reminders yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add anniversaries and important dates to remember
                </p>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full btn-press"
                  data-testid="add-first-reminder-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Reminder
                </Button>
              </div>
            ) : (
              <>
                {reminders.map((reminder) => {
                  const status = getReminderStatus(reminder.date);
                  const TypeIcon = REMINDER_TYPES.find(t => t.value === reminder.reminder_type)?.icon || Bell;
                  
                  return (
                    <div
                      key={reminder.id}
                      className="flex items-start gap-3 p-4 bg-background rounded-xl border border-charcoal/5"
                      data-testid={`reminder-${reminder.id}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${status.bg}`}>
                        <TypeIcon className={`w-5 h-5 ${status.color}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-charcoal">{reminder.title}</h4>
                          {reminder.recurring && (
                            <Repeat className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(reminder.date), "MMMM d, yyyy")}
                        </p>
                        <span className={`text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                        {reminder.notes && (
                          <p className="text-sm text-muted-foreground mt-1">{reminder.notes}</p>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(reminder.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        data-testid={`delete-reminder-${reminder.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </ScrollArea>

        {!showAddForm && reminders.length > 0 && (
          <div className="p-4 border-t border-charcoal/5">
            <Button
              onClick={() => setShowAddForm(true)}
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-xl h-11"
              data-testid="add-reminder-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Reminder
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RemindersPanel;
